import { FrontDynamicThemesCSS, IconGroupsPrefixes, IconGroupWithPrefix } from './client-themes.const';
import {
  BackgroundImages,
  BackgroundPatternImages,
  GameMainImages,
  GamePortraitImages,
  Palettes,
} from './general-params';
import { Palette } from './palette';
import { CustomImage, CustomSound, Font } from './style';
import { CoursizStrategy } from '../coursiz/coursiz-edited';
import { UserTypes } from './user';
import { ScoreFindStrategy } from './progression';
import { Tag } from './tag';

export enum ClientTypeEnum {
  JUMP = 'JUMP',
  BIZNESS = 'BIZNESS',
  OF = 'OF',
  INDEPENDANT = 'INDEPENDANT',
  ENTREPRISE_FO = 'ENTREPRISE_FO',
  ENTREPRISE_BO = 'ENTREPRISE_BO',
  ENTREPRISE_BOFO = 'ENTREPRISE_BOFO',
  SAP = 'SAP',
}

export class Client {
  id: number;
  slug: string;
  name: string;
  active: boolean;
  locale: string;
  address: string;
  postalCode: string;
  hasSignature: boolean;
  city: string;
  country: string;
  contact: string;
  logo: string;
  picto: string;
  type: ClientTypeEnum | string;
  clients: Client[];
  roles: string[];
  endLicence: Date;
  licenceContent: LicenceContent;
  createdAt: Date;
  updatedAt: Date;
  formules: string[];
  addressAdditional?: string;
  dataKey?: {
    nbModulesPublish: number;
    nbUsersTypes: Record<UserTypes, number>;
  };
  numberClients?: number;
  params?: ClientParams;
  signatureInfo?: string;
  wsDocument?: string;
  hasSubClients: boolean;
  ssoTracking: boolean;
  config: ClientConfig;
  skills?: ClientSkill[];
  tags: Tag[];

  constructor(client?: Client) {
    this.licenceContent = new LicenceContent(client?.licenceContent);
    this.contact = client?.contact;
    this.id = client?.id;
    this.slug = client?.slug;
    this.name = client?.name || '';
    this.active = client?.active;
    this.locale = client?.locale ?? 'en_EN';
    this.address = client?.address || '';
    this.addressAdditional = client?.addressAdditional || '';
    this.postalCode = client?.postalCode || '';
    this.city = client?.city || '';
    this.country = client?.country || '';
    this.hasSignature = client?.hasSignature;
    this.logo = client?.logo || '';
    this.picto = client?.picto || '';
    this.type = client?.type || '';
    this.clients = (client?.clients || []).map(c => new Client(c));
    this.endLicence = client?.endLicence ? new Date(client.endLicence) : null;
    this.createdAt = client?.createdAt ? new Date(client.createdAt) : null;
    this.updatedAt = client?.updatedAt ? new Date(client.updatedAt) : null;
    this.wsDocument = client?.wsDocument;
    this.formules = (client?.formules || []).map(f => f);
    this.signatureInfo = client?.signatureInfo;
    this.hasSubClients = client?.hasSubClients ?? false;
    this.ssoTracking = client?.ssoTracking ?? false;
    this.config = client?.config || { importAuto: false };
    this.skills = client?.skills || [];
    this.tags = client?.tags || [];

    //* Catch PHP fail the back send us an object when the list is empty
    //* so if it's not an array we initialise the value ourselves.
    if (Array.isArray(client?.roles)) {
      this.roles = [...client.roles];
    } else if (client?.roles) {
      this.roles = [client.roles];
    } else {
      this.roles = [];
    }

    this.dataKey = client?.dataKey;
    this.numberClients = client?.numberClients;
    this.params = new ClientParams(client?.params);
  }
}

export class Affectation {
  id: number;
  label: string;
  rank?: number;

  constructor(affectation?: Affectation) {
    this.id = affectation?.id || null;
    this.label = affectation?.label || null;
    this.rank = affectation?.rank;
  }
}

export class Criterion {
  id: number;
  label: string;
  main?: boolean;

  constructor(criterion?: Criterion) {
    if (criterion) {
      this.id = criterion.id;
      this.label = criterion.label;
      this.main = criterion.main;
    } else {
      this.id = null;
      this.label = null;
    }
  }
}

export class ClientParams {
  palette: Palette;
  mainFont: Font;
  customFonts?: Font[];
  customColors?: Palette[];
  learnizParams: ClientLearnizParams;
  gameParams: GamesParams;
  coursizBackground: CustomImage;
  coursizCustomBackgroundImages: CustomImage[];
  faviconUrl: CustomImage;
  customFaviconUrl: CustomImage[];
  frontDynamicParams?: FrontDynamicParams;
  isChatExpertEnabled: boolean;
  hidePrivacy?: boolean;
  hideHelpLearniz?: boolean;
  strategy: CoursizStrategy;

  constructor(params?: ClientParams) {
    this.palette = new Palette(params?.palette || Palettes[0]);
    this.mainFont = params?.mainFont?.family
      ? new Font(params?.mainFont)
      : {
          family: 'Montserrat',
        };
    this.customFonts = (params?.customFonts || []).map(f => new Font(f));
    this.customColors = (params?.customColors || []).map(c => new Palette(c));
    this.customFaviconUrl = (params?.customFaviconUrl || []).map(c => new CustomImage(c));
    this.faviconUrl = params?.faviconUrl ? new CustomImage(params.faviconUrl) : null;
    this.learnizParams = new ClientLearnizParams(params?.learnizParams);
    this.gameParams = new GamesParams(params?.gameParams);
    this.coursizBackground = new CustomImage(params?.coursizBackground || BackgroundImages[0]);
    this.coursizCustomBackgroundImages = (params?.coursizCustomBackgroundImages || []).map(i => new CustomImage(i));
    this.frontDynamicParams = new FrontDynamicParams(params?.frontDynamicParams);
    this.isChatExpertEnabled = params?.isChatExpertEnabled ?? false;
    this.hidePrivacy = params?.hidePrivacy ?? false;
    this.hideHelpLearniz = params?.hideHelpLearniz ?? false;
    this.strategy = params?.strategy ?? CoursizStrategy.DEFAULT;
  }
}

export class ProductParams {
  customMainImages?: CustomImage[];
  customPortraitImages?: CustomImage[];
  customSounds?: CustomSound[];

  constructor(pParams?: ProductParams) {
    this.customMainImages = (pParams?.customMainImages || []).map(i => new CustomImage(i));
    this.customPortraitImages = (pParams?.customPortraitImages || []).map(i => new CustomImage(i));
    this.customSounds = (pParams?.customSounds || []).map(i => new CustomSound(i));
  }
}

export class ClientConfig {
  importAuto: boolean;
}

export class ClientLearnizParams extends ProductParams {
  fontTitle?: Font;
  landingBackgroundImages: {
    main?: CustomImage;
    menu?: CustomImage;
  };
  slideBackground?: CustomImage;
  customMenuImages?: CustomImage[];
  /*customMainImages?: CustomImage[]; done in ProductParams */
  customSlideImages?: CustomImage[];

  constructor(learnizParams?: ClientLearnizParams) {
    super(learnizParams);
    this.fontTitle = new Font(learnizParams?.fontTitle);
    this.landingBackgroundImages = {
      main: new CustomImage(learnizParams?.landingBackgroundImages?.main || BackgroundImages[0]),
      menu: new CustomImage(learnizParams?.landingBackgroundImages?.menu || BackgroundPatternImages[0]),
    };
    this.slideBackground = new CustomImage(learnizParams?.slideBackground || BackgroundPatternImages[0]);
    this.customMenuImages = (learnizParams?.customMenuImages || []).map(i => new CustomImage(i));
    this.customSlideImages = (learnizParams?.customSlideImages || []).map(i => new CustomImage(i));
  }
}

export class GamesParams extends ProductParams {
  backgroundUrl?: CustomImage;
  portraitUrl?: CustomImage;
  faviconUrl?: string;
  soundUrl?: CustomSound;
  scoreStrategy?: ScoreFindStrategy;

  constructor(gParams?: GamesParams) {
    if (gParams) {
      super(gParams);
      this.backgroundUrl = new CustomImage(gParams.backgroundUrl || GameMainImages[0]);
      this.portraitUrl = new CustomImage(gParams.portraitUrl || GamePortraitImages[0]);
      this.faviconUrl = gParams.faviconUrl;
      this.soundUrl = new CustomSound(gParams.soundUrl);
      this.scoreStrategy = gParams.scoreStrategy;
    } else {
      super();
      this.backgroundUrl = GameMainImages[0];
      this.portraitUrl = GamePortraitImages[0];
    }
  }
}

export interface ClientFilter {
  clientId?: number;
  limit?: number | string;
  page?: number;
  forTargets?: boolean;
  order: string;
  name: string;
  type: string;
  createdAt: string;
  endLicence: string;
}

export enum ClientPossibleOrder {
  TRI_NAME_DESC = 'triNameDesc',
  TRI_NAME_ASC = 'triNameAsc',
  TRI_TYPE_ASC = 'triTypeAsc',
  TRI_TYPE_DESC = 'triTypeDesc',
  TRI_CREATED_AT_DESC = 'triCreateAtDesc',
  TRI_CREATED_AT_ASC = 'triCreateAtAsc',
  TRI_END_LICENCE_AT_ASC = 'triEndLicenceAtAsc',
  TRI_END_LICENCE_AT_DESC = 'triEndLicenceAtDesc',
}

export class FrontDynamicParams {
  themesCSS: FrontDynamicThemesCSS;
  icons: IconGroupWithPrefix;
  defaultParams: boolean; // if true apply the property written below
  font: Font;
  palette: Palette;
  favicon: CustomImage; //path
  customPatternImages: CustomImage[];
  customLandingImages: CustomImage[];
  customLogoImages: CustomImage[];
  backgroundLandingImage: CustomImage;
  backgroundPatternImage: CustomImage; //trame de fond
  tabLabel: string;
  specificThemingOvf: SpecificThemingOvf;

  constructor(params?: FrontDynamicParams) {
    this.themesCSS = params?.themesCSS || FrontDynamicThemesCSS.DEFAULT;
    this.icons = params?.icons || IconGroupsPrefixes[0];
    this.defaultParams = params?.defaultParams !== null ? params?.defaultParams : true;
    this.font = params?.font?.family
      ? new Font(params?.font)
      : {
          family: 'Montserrat',
        };
    this.palette = new Palette(params?.palette || Palettes[0]);
    this.favicon = new CustomImage(params?.favicon);
    this.customLandingImages = (params?.customLandingImages || []).map(i => new CustomImage(i));
    this.customPatternImages = (params?.customPatternImages || []).map(i => new CustomImage(i));
    this.customLogoImages = (params?.customLogoImages || []).map(i => new CustomImage(i));
    this.backgroundLandingImage = new CustomImage(params?.backgroundLandingImage);
    this.backgroundPatternImage = new CustomImage(params?.backgroundPatternImage);
    this.tabLabel = params?.tabLabel || 'FrontDynamic';
    this.specificThemingOvf = new SpecificThemingOvf(params?.specificThemingOvf);
  }
}

export class SpecificThemingOvf {
  primary: string;
  primaryLight: string;
  primaryDark: string;
  white: string;
  dark: string;
  navy: string;
  gray: string;
  progress: string;
  pending: string;
  done: string;
  titleFont: Font;
  mainFont: Font;
  backgroundHome: CustomImage;
  backgroundStats: CustomImage;

  constructor(themingOvf: SpecificThemingOvf) {
    this.primary = themingOvf?.primary || '#ffffff';
    this.primaryLight = themingOvf?.primaryLight || '#ffffff';
    this.primaryDark = themingOvf?.primaryDark || '#ffffff';
    this.white = themingOvf?.white || '#ffffff';
    this.dark = themingOvf?.dark || '#ffffff';
    this.navy = themingOvf?.navy || '#ffffff';
    this.gray = themingOvf?.gray || '#ffffff';
    this.progress = themingOvf?.progress || '#ffffff';
    this.pending = themingOvf?.pending || '#ffffff';
    this.done = themingOvf?.done || '#ffffff';
    this.titleFont = themingOvf?.titleFont?.family
      ? new Font(themingOvf?.titleFont)
      : {
          family: 'Montserrat',
          cssName: '"Montserrat"',
        };
    this.mainFont = themingOvf?.mainFont?.family
      ? new Font(themingOvf?.mainFont)
      : {
          family: 'Montserrat',
          cssName: '"Montserrat"',
        };
    this.backgroundHome = new CustomImage(themingOvf?.backgroundHome);
    this.backgroundStats = new CustomImage(themingOvf?.backgroundStats);
  }
}

export class LicenceContent {
  demo?: boolean;
  licences: Licence[];

  constructor(raw?: LicenceContent) {
    this.demo = raw?.demo;
    this.licences = (raw?.licences || []).map(l => new Licence(l));
  }
}

export class Licence {
  licenceInformation: LicenceInformation;

  constructor(raw?: Licence) {
    this.licenceInformation = new LicenceInformation(raw?.licenceInformation);
  }
}

class LicenceInformation {
  /** Current stats relative to the client (actual count) */
  count: LicenceThreshold[];

  /** Threshold allowed by the client's licence */
  licence: LicenceThreshold[];

  constructor(raw?: LicenceInformation) {
    this.count = (raw?.count || []).map(t => new LicenceThreshold(t));
    this.licence = (raw?.licence || []).map(t => new LicenceThreshold(t));
  }
}

class LicenceThreshold {
  unity: LicenceThresholdType;
  quantity: number;

  constructor(raw: LicenceThreshold) {
    Object.assign(this, raw);
  }
}

export interface ClientSkillFolder {
  slug: string;
  label: string;
  createdAt: string;
  updatedAt: string;
  parentFolderSlug?: string;
  hasFolderChild?: boolean;
  skills?: ClientSkill[];
}

export interface ClientSkill {
  slug: string;
  label: string;
  description?: string;
  imgSrc?: string;
  folderSlug?: string;
  createdAt: string;
  updatedAt: string;
}

export interface LicenceData {
  demo: boolean;
  cleaning?: boolean;
  licence: LicenceDataContent;
}

export interface LicenceDataContent {
  nbUser: number;
  nbClient: number;
  nbEditor: number;
  stockage: number;
  dateEnd: Date;
}

export enum LicenceThresholdType {
  NB_CLIENT = 'nbClient',
  NB_USER = 'nbUser',
  NB_EDITOR = 'nbEditor',
  STOCKAGE = 'stockage',
}
